import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_LABORATORYDEPARTMENT_LOAD,
    ACTION_LABORATORYDEPARTMENT_LOAD_BY_ID,
    ACTION_LABORATORYDEPARTMENTS_LOAD_BY_LABORATORYID,
    ACTION_LABORATORYDEPARTMENT_LOAD_FAILURE,
    ACTION_LABORATORYDEPARTMENT_LOAD_SUCCESS,

    ACTION_LABORATORYDEPARTMENT_SAVE,
    ACTION_LABORATORYDEPARTMENT_SAVE_SUCCESS,
    ACTION_LABORATORYDEPARTMENT_SAVE_FAILURE,

    ACTION_LABORATORYDEPARTMENT_CREATE,
    ACTION_LABORATORYDEPARTMENT_CREATE_SUCCESS,
    ACTION_LABORATORYDEPARTMENT_CREATE_FAILURE,

    ACTION_LABORATORYDEPARTMENT_DELETE,
    ACTION_LABORATORYDEPARTMENT_DELETE_SUCCESS,
    ACTION_LABORATORYDEPARTMENT_DELETE_FAILURE,

    ACTION_LABORATORYDEPARTMENT_VALIDATE,
    ACTION_LABORATORYDEPARTMENT_VALIDATE_SUCCESS,
    ACTION_LABORATORYDEPARTMENT_VALIDATE_FAILURE,

    ACTION_LABORATORYDEPARTMENT_CLEAR,
    ACTION_LABORATORYDEPARTMENT_FILTER,

    LaboratoryDepartmentAction
} from '../actions/laboratoryDepartments';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ILaboratoryDepartmentState {
    formData: Dtos.LaboratoryDepartment | Dtos.LaboratoryDepartment[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    deleteState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.LaboratoryDepartment[];
    formLength: number;
}

const initialState: ILaboratoryDepartmentState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    deleteState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ILaboratoryDepartmentState, _isLoading: boolean): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ILaboratoryDepartmentState,
    formData: Dtos.LaboratoryDepartment | Dtos.LaboratoryDepartment[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ILaboratoryDepartmentState, responseStatus: Dtos.ResponseStatus): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ILaboratoryDepartmentState, formFilter: IFormFilter): ILaboratoryDepartmentState {

    const items: Dtos.LaboratoryDepartment[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.pathologyDepartment?.name?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ILaboratoryDepartmentState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ILaboratoryDepartmentState, propertiesToClear?: string[]): ILaboratoryDepartmentState {

    if (!propertiesToClear) {
        const newState: ILaboratoryDepartmentState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ILaboratoryDepartmentState, _isSaving: boolean): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ILaboratoryDepartmentState,
    formData: Dtos.LaboratoryDepartment | Dtos.LaboratoryDepartment[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ILaboratoryDepartmentState, responseStatus: Dtos.ResponseStatus): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ILaboratoryDepartmentState, _isCreating: boolean): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ILaboratoryDepartmentState,
    formData: Dtos.LaboratoryDepartment,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ILaboratoryDepartmentState, responseStatus: Dtos.ResponseStatus): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function deleting(state: ILaboratoryDepartmentState, _isDeleting: boolean): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function deleteSuccess(
    state: ILaboratoryDepartmentState,
): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function deleteFailure(state: ILaboratoryDepartmentState, responseStatus: Dtos.ResponseStatus): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ILaboratoryDepartmentState, _isValidating: boolean): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ILaboratoryDepartmentState,
    formData: Dtos.LaboratoryDepartment,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ILaboratoryDepartmentState, responseStatus: Dtos.ResponseStatus): ILaboratoryDepartmentState {
    const newState: ILaboratoryDepartmentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const LaboratoryDepartmentReducer: Reducer<ILaboratoryDepartmentState> = (state: ILaboratoryDepartmentState = initialState, action: LaboratoryDepartmentAction) => {
    switch (action.type) {
        case ACTION_LABORATORYDEPARTMENT_LOAD:
        case ACTION_LABORATORYDEPARTMENT_LOAD_BY_ID:
        case ACTION_LABORATORYDEPARTMENTS_LOAD_BY_LABORATORYID:
            return loading(state, true);
        case ACTION_LABORATORYDEPARTMENT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_LABORATORYDEPARTMENT_SAVE:
            return saving(state, true);
        case ACTION_LABORATORYDEPARTMENT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_CREATE:
            return creating(state, true);
        case ACTION_LABORATORYDEPARTMENT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_LABORATORYDEPARTMENT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_DELETE:
            return deleting(state, true);
        case ACTION_LABORATORYDEPARTMENT_DELETE_SUCCESS:
            return deleteSuccess(state);
        case ACTION_LABORATORYDEPARTMENT_DELETE_FAILURE:
            return deleteFailure(state, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_VALIDATE:
            return validating(state, true);
        case ACTION_LABORATORYDEPARTMENT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_LABORATORYDEPARTMENT_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default LaboratoryDepartmentReducer;